import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../config/environment';
import { trackFormSubmission, trackEventWithUTM } from '../../utils/analytics';
import './ContactUs.css';
import SEOHead from '../SEOHead';

// reCAPTCHA types
declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  enquiryType: string;
  message: string;
}

const ContactUs: React.FC = () => {
  const { t } = useTranslation();
  
  // Immediate scroll to top on component render
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    email: '',
    enquiryType: 'General',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  
  // reCAPTCHA v3 - no ref needed, no loading state needed
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        setRecaptchaLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      };
      document.head.appendChild(script);
    };

    loadRecaptcha();
  }, []);


  const enquiryTypes = [
    { key: 'General', label: t('contactUs.enquiryTypes.general') },
    { key: 'Service Inquiry', label: t('contactUs.enquiryTypes.serviceInquiry') },
    { key: 'Technical Support', label: t('contactUs.enquiryTypes.technicalSupport') },
    { key: 'Partnership', label: t('contactUs.enquiryTypes.partnership') },
    { key: 'Feedback', label: t('contactUs.enquiryTypes.feedback') },
    { key: 'Complaint', label: t('contactUs.enquiryTypes.complaint') },
    { key: 'Other', label: t('contactUs.enquiryTypes.other') }
  ];

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = t('contactUs.validation.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('contactUs.validation.nameMinLength');
    } else if (formData.name.trim().length > 50) {
      errors.name = t('contactUs.validation.nameMaxLength');
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      errors.name = t('contactUs.validation.nameOnlyLetters');
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = t('contactUs.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('contactUs.validation.emailInvalid');
    }

    // Phone validation - exactly 10 digits
    if (!formData.phone.trim()) {
      errors.phone = t('contactUs.validation.phoneRequired');
    } else {
      // Remove all non-digit characters for validation
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.phone = t('contactUs.validation.phoneExact10Digits');
      } else if (!/^[0-9]{10}$/.test(phoneDigits)) {
        errors.phone = t('contactUs.validation.phoneOnlyDigits');
      }
    }

    // Message validation - max 250 characters
    if (!formData.message.trim()) {
      errors.message = t('contactUs.validation.messageRequired');
    } else if (formData.message.trim().length < 10) {
      errors.message = t('contactUs.validation.messageMinLength');
    } else if (formData.message.trim().length > 250) {
      errors.message = t('contactUs.validation.messageMaxLength');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Get reCAPTCHA v3 token
    let recaptchaToken = '';
    try {
      if (window.grecaptcha && recaptchaLoaded) {
        recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'contact_form'
        });
      } else {
        throw new Error('reCAPTCHA not loaded');
      }
    } catch (error) {
      setErrorMessage('reCAPTCHA verification failed. Please refresh the page and try again.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch(`${config.authServerUrl}/contact/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackFormSubmission('contact_form', true);
        trackEventWithUTM('contact_form_submitted', 'contact', formData.enquiryType);
        setFormData({
          name: '',
          phone: '',
          email: '',
          enquiryType: 'General',
          message: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Contact form submission failed:', err);
      setSubmitStatus('error');
      trackFormSubmission('contact_form', false);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-us">
      <SEOHead
        title="Contact Us – Get in Touch with Antaryatra"
        description="Have questions about booking a puja or Acharya selection? Contact Antaryatra's support team for help with ceremony planning, muhurat guidance and more."
        canonicalPath="/contact"
        keywords="contact Antaryatra, puja booking support, Acharya helpline, book pandit Delhi NCR"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Antaryatra",
          "url": "https://www.antaryatra.org/contact",
          "description": "Reach out to Antaryatra for puja bookings, Acharya selection, and ceremony planning support.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Antaryatra",
            "url": "https://www.antaryatra.org",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "availableLanguage": ["Hindi", "English"]
            }
          }
        }}
      />
      <div className="contact-hero">
        <div className="contact-hero-content">
          <h1>{t('contactUs.heroTitle')}</h1>
          <p>{t('contactUs.heroSubtitle')}</p>
        </div>
      </div>

      <div className="contact-container">
        <div className="contact-content">
          {/* Contact Information Section */}
          <div className="contact-info">
            <h2>{t('contactUs.getTouchTitle')}</h2>
            <p className="contact-subtitle">
              {t('contactUs.getTouchSubtitle')}
            </p>

            <div className="contact-details">
              <div className="contact-item">
                {/* <div className="contact-icon">📞</div>
                <div className="contact-text">
                  <h3>Phone</h3>
                  <p>+91 98765 43210</p>
                  <p>Mon - Fri: 9:00 AM - 6:00 PM</p>
                </div> */}
              </div>

              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div className="contact-text">
                  <h3>{t('contactUs.email')}</h3>
                  <p>{t('contactUs.emailAddress')}</p>
                  <p>{t('contactUs.emailResponse')}</p>
                </div>
              </div>

              <div className="contact-item">
                {/* <div className="contact-icon">📍</div>
                <div className="contact-text">
                  <h3>Office</h3>
                  <p>123 Service Street</p>
                  <p>Mumbai, Maharashtra 400001</p>
                </div> */}
              </div>

              <div className="contact-item">
                {/* <div className="contact-icon">💬</div>
                <div className="contact-text">
                  <h3>Live Chat</h3>
                  <p>Available on our website</p>
                  <p>Mon - Fri: 9:00 AM - 6:00 PM</p>
                </div> */}
              </div>
            </div>

            <div className="social-links">
              <h3>{t('contactUs.followUs')}</h3>
              <div className="social-icons">
                <a href="https://www.facebook.com/share/1FBf49bgPx/" aria-label="Facebook" className="social-icon facebook" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://x.com/_Antaryatra_" aria-label="X (Twitter)" className="social-icon twitter" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/antaryatraofficial?igsh=bXJueHNienZrYjZs" aria-label="Instagram" className="social-icon instagram" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.youtube.com/@Antaryatrathesacredjourney" aria-label="YouTube" className="social-icon youtube" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="#" aria-label="LinkedIn" className="social-icon linkedin" target="_blank" rel="noopener noreferrer" style={{ display: 'none' }}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-form-section">
            <h2>{t('contactUs.sendMessageTitle')}</h2>
            <p className="form-subtitle">
              {t('contactUs.formSubtitle')}
            </p>

            {submitStatus === 'success' && (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h3>{t('contactUs.successTitle')}</h3>
                <p>{t('contactUs.successMessage')}</p>
              </div>
            )}

            {submitStatus !== 'success' && (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contactUs.form.fullName')} *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={validationErrors.name ? 'error' : ''}
                      placeholder={t('contactUs.form.fullNamePlaceholder')}
                      maxLength={50}
                    />
                    {validationErrors.name && (
                      <span className="error-text">{validationErrors.name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">{t('contactUs.form.phoneNumber')} * {t('contactUs.form.phoneNumberNote')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={validationErrors.phone ? 'error' : ''}
                      placeholder={t('contactUs.form.phoneNumberPlaceholder')}
                      maxLength={10}
                    />
                    {validationErrors.phone && (
                      <span className="error-text">{validationErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">{t('contactUs.form.emailAddress')} *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={validationErrors.email ? 'error' : ''}
                      placeholder={t('contactUs.form.emailAddressPlaceholder')}
                    />
                    {validationErrors.email && (
                      <span className="error-text">{validationErrors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="enquiryType">{t('contactUs.form.enquiryType')}</label>
                    <select
                      id="enquiryType"
                      name="enquiryType"
                      value={formData.enquiryType}
                      onChange={handleInputChange}
                    >
                      {enquiryTypes.map(type => (
                        <option key={type.key} value={type.key}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contactUs.form.message')} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={validationErrors.message ? 'error' : ''}
                    placeholder={t('contactUs.form.messagePlaceholder')}
                    rows={5}
                    maxLength={250}
                  />
                  <div className="message-counter">
                    <span className={formData.message.length > 250 ? 'counter-error' : ''}>
                      {formData.message.length}/250 {t('contactUs.form.charactersCount')}
                    </span>
                  </div>
                  {validationErrors.message && (
                    <span className="error-text">{validationErrors.message}</span>
                  )}
                </div>

                {errorMessage && (
                  <div className="error-message">
                    <div className="error-icon">❌</div>
                    <p>{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      {t('contactUs.form.sendingMessage')}
                    </>
                  ) : (
                    t('contactUs.form.sendMessage')
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;